import { Button, Flex, Tooltip } from 'antd';
import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Document, pdfjs, Page } from 'react-pdf';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';

import FilePreview from 'components/filePreview/filePreview';
import { downloadContractUrlRequest, getContractStringLink, getContractUrlRequest } from '../services/agreementService';
import NotificationService from 'core/services/notification';
import LoadingComponent from 'components/loadingComponent/loadingComponent';
import { useDispatch } from 'react-redux';
import { setCompany } from 'stores/useCompanyStore';
import { getViewerWidth } from 'utils/fileViewerWidth';
import { isRTL } from 'core/services/language';
import { downloadFile } from 'utils/downloadFile';

pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.mjs`;

export default function AgreementRegister() {
	const navigate = useNavigate();
	const { t } = useTranslation();
	const dispatch = useDispatch();

	const [contractUrl, setContractUrl] = useState<string>();
	const [isLoading, setIsLoading] = useState(false);
	const [isDownloading, setIsDownloading] = useState(false);
	const [isDownloaded, setIsDownloaded] = useState(false);
	const [numOfPages, setNumOfPages] = useState<number>(0);
	const [containerWidth, setContainerWidth] = useState<number>(getViewerWidth());

	useEffect(() => {
		onGetContractLink();

		const handleResize = () => {
			setContainerWidth(getViewerWidth());
		};

		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, []);

	const onGetContractLink = async () => {
		// if user redirect from plans page.
		if (getContractStringLink()) {
			setContractUrl(getContractStringLink());
			return;
		}

		// if user access agreement page direct.
		try {
			setIsLoading(true);

			const { platform_use_contract_template } = await getContractUrlRequest();

			setContractUrl(platform_use_contract_template);

			setIsLoading(false);
		} catch (error) {
			setIsLoading(false);
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.FETCH_ERROR'));
		}
	};

	const onDownloadContract = async () => {
		try {
			setIsDownloading(true);
			const { company } = await downloadContractUrlRequest();

			dispatch(setCompany(company));

			await downloadFile(company.platformUseContractTemplate, isRTL() ? 'إتفاقية إستخدام خدمات حفظ.pdf' : 'Hefth Services Use Agreement.pdf');

			setIsDownloading(false);
			setIsDownloaded(true);
		} catch (error) {
			setIsDownloading(false);
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.FETCH_ERROR'));
		}
	};

	const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
		setNumOfPages(numPages);
	};

	return (
		<>
			<motion.h2
				initial={{ opacity: 0, y: 20 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{
					duration: 0.5,
					delay: 0.3,
				}}
				key="title">
				{t('REGISTER.THANKS_FOR_SUBSCRIPTION')}
			</motion.h2>

			<motion.p
				initial={{ opacity: 0, y: 20 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{
					duration: 0.5,
					delay: 0.5,
				}}
				key="subTitle"
				className="text-light">
				{t('REGISTER.PLEASE_DOWNLOAD')}
			</motion.p>

			<motion.div
				initial={{ opacity: 0, y: 20 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{
					duration: 0.5,
					delay: 0.8,
				}}
				key="form"
				style={{ textAlign: 'start' }}>
				{isLoading ? (
					<LoadingComponent />
				) : (
					<Document file={contractUrl} onLoadSuccess={onDocumentLoadSuccess} className="agreementPdf">
						{Array.from({ length: numOfPages }, (_, i) => (
							<Page pageNumber={i + 1} scale={1} width={containerWidth} key={i} />
						))}
					</Document>
				)}
				<div className="m-auto" style={{ width: getViewerWidth() }}>
					<FilePreview name={t('REGISTER.HEFTH_SERVICES_AGREEMENTS')} downloadLink={contractUrl} className="my-4" onDownload={onDownloadContract} />

					<Flex justify="flex-end">
						<Tooltip title={isDownloaded ? '' : t('REGISTER.DOWNLOAD_FIRST')} color="#EE4B2B">
							<Button type="primary" ghost disabled={isDownloading || !isDownloaded} onClick={() => navigate('/dashboard')}>
								{t('COMMON.NEXT')}
							</Button>
						</Tooltip>
					</Flex>
				</div>
			</motion.div>
		</>
	);
}
